import { IonContent, isPlatform } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { InfoProtection } from '@/components/InfoProtection';
import { useAccount } from '@/hooks/useAccount';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context.tsx';
import { GREAT_CHOICE_IMAGES } from '@/pages/Onboarding/options/SettingSelector/SETTING_SELECTOR_OPTIONS';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';
import { canLogEvents } from '@/utils/canLogEvents';

import { layoutQueryParams } from '../../params';
import { LayoutDataType } from '../../types';

import { EmailStyles } from './Email.styles';

export const ScreenEmail: React.FC = () => {
  const [isInputFocused, setIsInputFocused] = React.useState(false);

  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Email',
      });
    }

    layoutQueryParams.removeStepsData();
  }, []);

  const { refreshUser } = useAccount();
  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const settingSelector = form.watch('setting_selector');

  const getBackgroundImage = () => {
    if (!settingSelector) return '';

    const selectedOption = GREAT_CHOICE_IMAGES.find(
      (option) => option.id === settingSelector,
    );

    if (selectedOption) {
      return selectedOption.spriteSrc;
    }

    return '';
  };

  const inputRef = React.useRef<HTMLIonInputElement>(null);

  const formEmail = form.watch('email');

  const isValidEmail = checkIsValidateEmail(formEmail);

  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  React.useEffect(() => {
    if (isPlatform('android') || isPlatform('desktop')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      });
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      }, 1000);
    }
  }, []);

  const handleNextStep = async (): Promise<void> => {
    if (!formEmail || !isValidEmail) return;

    try {
      const lowerCaseEmail = formEmail.toLowerCase().trim();

      trackOnboardingComplete({ email: lowerCaseEmail });

      await Promise.all([
        createStripeClient(lowerCaseEmail),
        userService.createOrUpdate({
          onboarding_data: {
            onboarded: true,
          },
        }),
      ]);

      refreshUser();
    } catch (error) {
      console.error('Can not create a stripe user', error);
    }
  };

  const handleBack = () => {
    wizard.goToStep('twist_generator');
  };

  const handleInputFocus = () => setIsInputFocused(true);
  const handleInputBlur = () => setIsInputFocused(false);

  return (
    <EmailStyles
      backgroundImage={getBackgroundImage()}
      isFocused={isInputFocused}
    >
      <IonContent>
        <OnboardingNavigation isRenderBackButton={true} onBack={handleBack} />
        <OnboardingTitle>One magical night, designed by you...</OnboardingTitle>
        <OnboardingSubtitle>
          Want to keep it forever? Enter your
          <br />
          email to get the full story
        </OnboardingSubtitle>
        <div className="email-content-wrapper">
          <div className="email-input-container">
            <FormInput
              form={form}
              hidden={false}
              onSubmit={handleNextStep}
              disableButton={!isValidEmail}
              ref={inputRef}
              name="email"
              type="email"
              placeholder="Type your email"
              loading={isCreatingStripeClient}
              theme={'primary'}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
          </div>
          <div className="email-protection">
            <InfoProtection />
          </div>
        </div>
      </IonContent>
    </EmailStyles>
  );
};
